// components/brand/BrandPhotoMasonry.js - Updated with 1 column mobile, 2 columns for desktop
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SinglePromotionPopup from '../promotion/SinglePromotionPopup';

function BrandPhotoMasonry({ promotions, brandName }) {
  const [columns, setColumns] = useState(getInitialColumns());
  const [imagesLoaded, setImagesLoaded] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [selectedPromotion, setSelectedPromotion] = useState(null);

  // Function to determine initial column count based on viewport
  function getInitialColumns() {
    if (typeof window !== 'undefined') {
      return window.innerWidth >= 768 ? 2 : 1; // 2 columns for tablet & desktop, 1 for mobile
    }
    return 1; // Default for SSR
  }

  // Update column count on window resize
  useEffect(() => {
    const handleResize = () => {
      setColumns(window.innerWidth >= 768 ? 2 : 1);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Reset image loading counter when promotions change
  useEffect(() => {
    setImagesLoaded(0);
    setIsVisible(false);
  }, [promotions]);
  
  // Show masonry grid when all images are loaded or after a timeout
  useEffect(() => {
    if (promotionsWithImages && promotionsWithImages.length > 0 && imagesLoaded >= promotionsWithImages.length) {
      setIsVisible(true);
    }
    
    // Fallback: show grid after 2 seconds even if not all images loaded
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 2000);
    
    return () => clearTimeout(timer);
  }, [imagesLoaded]);
  
  // Handle image load event
  const handleImageLoad = () => {
    setImagesLoaded(prev => prev + 1);
  };

  // Open promotion popup
  const handlePromotionClick = (promotion) => {
    setSelectedPromotion(promotion);
    // Prevent scrolling when popup is open
    document.body.style.overflow = 'hidden';
  };

  // Close promotion popup
  const handleClosePopup = () => {
    setSelectedPromotion(null);
    // Re-enable scrolling
    document.body.style.overflow = 'auto';
  };

  // Handle click outside popup to close it
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClosePopup();
    }
  };

  if (!promotions || promotions.length === 0) {
    return (
      <div className="text-center p-6 bg-gray-50 rounded-lg">
        <p className="text-gray-500">No promotions available for this brand.</p>
      </div>
    );
  }

  // Filter out promotions without images
  const promotionsWithImages = promotions.filter(promo => promo.image);

  if (promotionsWithImages.length === 0) {
    return (
      <div className="text-center p-6 bg-gray-50 rounded-lg">
        <p className="text-gray-500">No promotion images available for this brand.</p>
      </div>
    );
  }

  // Helper function to check if a promotion has title content
  const hasTextContent = (promo) => {
    return (promo.title && promo.title.trim() !== '');
  };
  
  // Create columns for masonry grid
  const promotionColumns = [];
  for (let i = 0; i < columns; i++) {
    promotionColumns.push([]);
  }
  
  // Distribute promotions across columns - matching your Promotions.js approach
  promotionsWithImages.forEach((promo, index) => {
    const columnIndex = index % columns;
    promotionColumns[columnIndex].push(promo);
  });

  return (
    <>
      <div className={`px-0 transition-opacity duration-500 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
        {/* CSS for animations and styles */}
        <style jsx>{`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          .animate-fadeIn {
            animation: fadeIn 0.3s ease-in-out;
          }
          .promotion-card {
            transition: transform 0.2s ease-out, box-shadow 0.2s ease-out;
          }
          .promotion-card:hover {
            transform: translateY(-3px);
            box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
          }
        `}</style>
        
        {/* Masonry Grid - Using flex row like in your Promotions.js */}
        <div className="flex flex-row -mx-2">
          {/* Create columns */}
          {promotionColumns.map((columnPromotions, columnIndex) => (
            <div key={columnIndex} className={`px-2 w-full ${columns > 1 ? 'md:w-1/2' : ''}`}>
              {/* Promotions in this column */}
              {columnPromotions.map((promo, promoIndex) => (
                <div 
                  key={`${columnIndex}-${promoIndex}`} 
                  className="mb-4 bg-white rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => handlePromotionClick(promo)}
                >
                  {/* Promotion Image with potential Gradient Overlay for Title */}
                  <div className="relative">
                    <img
                      src={promo.image}
                      alt={promo.title || `${brandName} promotion`}
                      className="w-full h-auto object-cover"
                      onLoad={handleImageLoad}
                      loading="lazy"
                      style={{ 
                        aspectRatio: 'auto',
                        // Optional: Set max-height to create more masonry effect
                        maxHeight: hasTextContent(promo) ? '300px' : '220px'
                      }}
                    />
                    
                    {/* Category Badge (if available) */}
                    {promo.category && (
                      <div className="absolute top-2 right-2">
                        <span className="inline-block bg-blue-600 text-white text-xs px-2 py-1 rounded-full">
                          {promo.category}
                        </span>
                      </div>
                    )}
                    

                  </div>
                  
                  {/* Only show title if available */}
                  {promo.title && promo.title.trim() !== '' && (
                    <div className="p-4">
                      <h4 className="font-semibold text-lg mb-2">{promo.title}</h4>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
      
      {/* Promotion Detail Popup */}
      {selectedPromotion && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center p-4"
          style={{ animation: 'fadeIn 0.3s ease-out' }}
          onClick={handleOverlayClick}
        >
          <SinglePromotionPopup 
            promotion={selectedPromotion} 
            onClose={handleClosePopup} 
          />
        </div>
      )}
    </>
  );
}

export default BrandPhotoMasonry;